import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import {
  CommonInput,
  CommonSelect,
  CommonDatePicker,
  CommonButton,
  CommonModal,
} from "../Input";
import Swal from "sweetalert2";

const OrganizationCustomerModal = ({ show, onHide, customer }) => {
  const [formData, setFormData] = useState({
    beneficiaries: [{ id: Date.now() }],
  });
  const [files, setFiles] = useState({});

  const fileInputRefs = {
    signedForm: React.useRef(null),
    memorandum: React.useRef(null),
    acra: React.useRef(null),
    directorsId: React.useRef(null),
    authorisedId: React.useRef(null),
    chart: React.useRef(null),
  };

  useEffect(() => {
    if (show) {
      if (customer) {
        setFormData({
          fullName: customer.fullName || "",
          dob: customer.dob || "",
          email: customer.email || "",
          nationality: { label: customer.nationality, value: customer.nationality },
          idType: { label: customer.idType, value: customer.idType },
          idNumber: customer.idNumber || "",
          jobTitle: customer.jobTitle || "",
          residentialAddress: customer.residentialAddress || "",
          registeredName: customer.registeredName || "",
          uen: customer.uen || "",
          entityType: { label: customer.entityType, value: customer.entityType },
          incorporationDate: customer.incorporationDate || "",
          businessActivity: customer.businessActivity || "",
          phonePrefix: { label: "+65", value: "+65" },
          phone: customer.phone || "",
          primaryEmail: customer.primaryEmail || "",
          localBranches: customer.localBranches || "",
          overseasBranches: customer.overseasBranches || "",
          overseasCountries: customer.overseasCountries || "",
          country: { label: customer.country, value: customer.country },
          city: customer.city || "",
          addressLine1: customer.addressLine1 || "",
          addressLine2: customer.addressLine2 || "",
          postalCode: customer.postalCode || "",
          beneficiaries: (customer.beneficialOwners || []).map((o, idx) => ({
            id: idx,
            name: o.name,
            dob: o.dob,
            nationality: { label: o.nationality, value: o.nationality },
            idType: { label: o.idType, value: o.idType },
            idNumber: o.idNumber,
            address: o.address,
            interest: o.interest,
          })),
        });
        if (!customer.beneficialOwners?.length) {
           setFormData(prev => ({ ...prev, beneficiaries: [{ id: Date.now() }] }));
        }
      } else {
        setFormData({
          beneficiaries: [{ id: Date.now() }],
          phonePrefix: { label: "+65", value: "+65" },
        });
      }
    }
  }, [show, customer]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateBeneficiary = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      beneficiaries: prev.beneficiaries.map((b) =>
        b.id === id ? { ...b, [field]: value } : b
      ),
    }));
  };

  const options = {
    nationality: [
      { value: "singapore", label: "Singaporean" },
      { value: "vietnamese", label: "Vietnamese" },
    ],
    idType: [
      { value: "nric", label: "NRIC" },
      { value: "passport", label: "Passport" },
    ],
    entityType: [
      { value: "private", label: "Private Limited" },
      { value: "sole", label: "Sole Proprietorship" },
      { value: "other", label: "Others (please specify)" },
    ],
    country: [
      { value: "SG", label: "Singapore" },
      { value: "VN", label: "Vietnam" },
    ],
    phonePrefix: [
      { value: "+65", label: "+65" },
      { value: "+84", label: "+84" },
    ],
  };

  const handleFileClick = (refKey) => {
    fileInputRefs[refKey].current.click();
  };

  const handleFileChange = (refKey, e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null;
      setFiles((prev) => ({ ...prev, [refKey]: { file, preview } }));
    }
  };

  const addBeneficiary = () => {
    setFormData((prev) => ({
      ...prev,
      beneficiaries: [...prev.beneficiaries, { id: Date.now() }],
    }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    Swal.fire({
      icon: "success",
      title: "Success",
      text: customer 
        ? "Organization Customer updated successfully!" 
        : "Organization Customer created successfully!",
      confirmButtonColor: "var(--primary)",
    }).then(() => {
      onHide();
    });
  };

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      size="xl"
      title={customer ? "Edit Organization Customer" : "Create New Organization Customer"}
      headerClassName="border-bottom pb-3"
      footer={
        <>
          <CommonButton variant="outline" onClick={onHide} className="me-2">
            Close
          </CommonButton>
          <CommonButton variant="primary" onClick={handleSubmit}>
            {customer ? "Update Customer" : "Create Customer"}
          </CommonButton>
        </>
      }
    >
      <p className="mb-4 text-muted fs-14">
        {customer 
          ? "Update the organization customer information below." 
          : "Fill in the organization customer information to create a new organization customer."}
      </p>
      <form onSubmit={handleSubmit}>
        {/* Customer Type Section */}
        <div className="mb-4">
          <CommonInput
            label="Customer Type"
            value="Organization"
            readOnly
            wrapClass="w-25"
          />
        </div>

        {/* Personal Information (Main Contact) */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">
            Personal Information (Main Contact)
          </h6>
        </div>
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <CommonInput 
              label="Full Name" 
              placeholder="Enter full name" 
              value={formData.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonDatePicker 
              label="Date of Birth" 
              value={formData.dob}
              onChange={(val) => handleChange("dob", val)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput 
              label="Email" 
              placeholder="Enter email" 
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonSelect
              label="Nationality"
              placeholder="Select an option"
              options={options.nationality}
              value={formData.nationality}
              onChange={(val) => handleChange("nationality", val)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonSelect
              label="ID Type"
              placeholder="Select an option"
              options={options.idType}
              value={formData.idType}
              onChange={(val) => handleChange("idType", val)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput 
              label="ID Number" 
              placeholder="Enter ID number" 
              value={formData.idNumber}
              onChange={(e) => handleChange("idNumber", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput 
              label="Job Title" 
              placeholder="Enter job title" 
              value={formData.jobTitle}
              onChange={(e) => handleChange("jobTitle", e.target.value)}
            />
          </Col>
          <Col md={6} className="mb-3">
            <CommonInput
              label="Residential Address"
              placeholder="Enter residential address"
              value={formData.residentialAddress}
              onChange={(e) => handleChange("residentialAddress", e.target.value)}
            />
          </Col>
        </Row>

        {/* Organization Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Organization Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={4} className="mb-3">
            <CommonInput
              label="Registered Name"
              placeholder="Enter registered name"
              value={formData.registeredName}
              onChange={(e) => handleChange("registeredName", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="UEN/Registration Number"
              placeholder="Enter UEN/Registration Number"
              value={formData.uen}
              onChange={(e) => handleChange("uen", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Type of Entity"
              options={options.entityType}
              placeholder="Select an option"
              value={formData.entityType}
              onChange={(val) => handleChange("entityType", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Type of Entity (Specify if others)"
              placeholder="Select type of entity"
              value={formData.entityTypeSpecify}
              onChange={(e) => handleChange("entityTypeSpecify", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonDatePicker 
              label="Date of Incorporation" 
              value={formData.incorporationDate}
              onChange={(val) => handleChange("incorporationDate", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Business Activity"
              placeholder="Enter business activity"
              value={formData.businessActivity}
              onChange={(e) => handleChange("businessActivity", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <label className="text-black fw-bold mb-1">Phone Number</label>
            <div className="d-flex gap-2">
              <div style={{ width: "150px" }}>
                <CommonSelect 
                  options={options.phonePrefix} 
                  placeholder="+65" 
                  value={formData.phonePrefix}
                  onChange={(val) => handleChange("phonePrefix", val)}
                />
              </div>
              <div className="flex-grow-1">
                <CommonInput 
                  placeholder="Enter phone number" 
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label={
                <span>
                  Primary Email Address{" "}
                  <i className="fas fa-info-circle text-muted ms-1 fs-12"></i>
                </span>
              }
              placeholder="Enter primary email address"
              value={formData.primaryEmail}
              onChange={(e) => handleChange("primaryEmail", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Number of Local Branches"
              placeholder="Enter number of local branches"
              value={formData.localBranches}
              onChange={(e) => handleChange("localBranches", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Number of Overseas Branches"
              placeholder="Enter number of overseas branches"
              value={formData.overseasBranches}
              onChange={(e) => handleChange("overseasBranches", e.target.value)}
            />
          </Col>
          <Col md={8} className="mb-3">
            <CommonInput
              label="Countries Overseas Branches are Situated in"
              placeholder="Enter countries overseas branches are situated in"
              value={formData.overseasCountries}
              onChange={(e) => handleChange("overseasCountries", e.target.value)}
            />
          </Col>
        </Row>

        {/* Organization Address */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Organization Address</h6>
        </div>
        <Row className="mb-4">
          <Col md={2} className="mb-3">
            <CommonSelect
              label="Country"
              options={options.country}
              placeholder="Select an option"
              value={formData.country}
              onChange={(val) => handleChange("country", val)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 1"
              placeholder="Enter address line 1"
              value={formData.addressLine1}
              onChange={(e) => handleChange("addressLine1", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 2 (Optional)"
              placeholder="Enter address line 2"
              value={formData.addressLine2}
              onChange={(e) => handleChange("addressLine2", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput 
              label="City" 
              placeholder="Enter city" 
              value={formData.city}
              onChange={(e) => handleChange("city", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput 
              label="Postal Code" 
              placeholder="Enter postal code" 
              value={formData.postalCode}
              onChange={(e) => handleChange("postalCode", e.target.value)}
            />
          </Col>
        </Row>

        {/* Beneficial Owners */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Beneficial Owners</h6>
        </div>
        <div className="mb-3 px-1">
          <span className="fw-bold fs-14">
            Total: {formData.beneficiaries?.reduce((acc, b) => acc + Number(b.interest || 0), 0)}%
          </span>
        </div>

        {formData.beneficiaries?.map((b, index) => (
          <div
            key={b.id}
            className="border rounded p-3 mb-3 bg-white"
            style={{ position: "relative" }}
          >
            <h6 className="fw-bold mb-3 fs-14">Beneficiary {index + 1} ({b.interest || 0}%)</h6>
            <Row>
              <Col md={3} className="mb-3">
                <CommonInput 
                  label="Full Name" 
                  placeholder="Enter full name" 
                  value={b.name}
                  onChange={(e) => updateBeneficiary(b.id, "name", e.target.value)}
                />
              </Col>
              <Col md={3} className="mb-3">
                <CommonDatePicker 
                  label="Date of Birth" 
                  value={b.dob}
                  onChange={(val) => updateBeneficiary(b.id, "dob", val)}
                />
              </Col>
              <Col md={3} className="mb-3">
                <CommonSelect
                  label="Nationality"
                  options={options.nationality}
                  placeholder="Select an option"
                  value={b.nationality}
                  onChange={(val) => updateBeneficiary(b.id, "nationality", val)}
                />
              </Col>
              <Col md={3} className="mb-3">
                <CommonSelect
                  label="ID Type"
                  options={options.idType}
                  placeholder="Select an option"
                  value={b.idType}
                  onChange={(val) => updateBeneficiary(b.id, "idType", val)}
                />
              </Col>
              <Col md={3} className="mb-3">
                <CommonInput 
                  label="ID Number" 
                  placeholder="Enter ID number" 
                  value={b.idNumber}
                  onChange={(e) => updateBeneficiary(b.id, "idNumber", e.target.value)}
                />
              </Col>
              <Col md={3} className="mb-3">
                <CommonInput
                  label="Residential Address"
                  placeholder="Enter residential address"
                  value={b.address}
                  onChange={(e) => updateBeneficiary(b.id, "address", e.target.value)}
                />
              </Col>
              <Col md={3} className="mb-3">
                <CommonInput
                  label="Ownership Interest (%)"
                  placeholder="Enter ownership interest (%)"
                  value={b.interest}
                  onChange={(e) => updateBeneficiary(b.id, "interest", e.target.value)}
                />
              </Col>
            </Row>
          </div>
        ))}

        <div className="mb-4">
          <CommonButton variant="outline" onClick={addBeneficiary}>
            Add
          </CommonButton>
        </div>

        {/* Document Upload */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Document Upload</h6>
        </div>
        <Row className="mb-4">
          {[
            {
              id: "signedForm",
              label: "Signed PDF Form",
              desc: "Upload document",
              small: ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB)",
            },
            {
              id: "memorandum",
              label: "Memorandum & Article of Association",
              desc: "Upload document",
              small: ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB)",
            },
            {
              id: "acra",
              label: "ACRA Biz Profile",
              desc: "Upload document",
              small: ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB)",
            },
            {
              id: "directorsId",
              label: "Directors & Owners ID",
              desc: "Upload documents",
              small:
                ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB each, up to 10 files)",
            },
            {
              id: "authorisedId",
              label: "Authorised Persons ID",
              desc: "Upload documents",
              small:
                ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB each, up to 10 files)",
            },
            {
              id: "chart",
              label: "Organization Chart",
              desc: "Upload documents",
              small:
                ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB each, up to 10 files)",
            },
          ].map((box, i) => (
            <Col md={4} key={i} className="mb-4">
              <label className="text-black fw-bold mb-1 fs-13">
                {box.label}
              </label>
              <input
                type="file"
                className="d-none"
                ref={fileInputRefs[box.id]}
                onChange={(e) => handleFileChange(box.id, e)}
                accept=".jpg,.jpeg,.png,.heic,.heif,.pdf,.docx"
              />
              <div
                className="upload-box border-dashed rounded p-2 text-center d-flex flex-column align-items-center justify-content-center"
                style={{
                  border: files[box.id] ? "2px solid var(--primary)" : "2px dashed #d5dfe7",
                  cursor: "pointer",
                  minHeight: "100px",
                  backgroundColor: files[box.id] ? "#f0f9ff" : "#fcfdfe",
                  transition: "all 0.3s ease",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {files[box.id] && files[box.id].preview ? (
                  <div className="w-100 h-100 position-relative">
                    <img
                      src={files[box.id].preview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "80px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                    <div
                      className="position-absolute top-50 start-50 translate-middle w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25"
                      style={{ opacity: 0, transition: "0.3s" }}
                      onMouseOver={(e) => (e.currentTarget.style.opacity = 1)}
                      onMouseOut={(e) => (e.currentTarget.style.opacity = 0)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileClick(box.id);
                      }}
                    >
                      <span className="btn btn-xs btn-primary shadow-none">
                        Change
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="d-flex align-items-center w-100 h-100"
                    onClick={() => handleFileClick(box.id)}
                  >
                    <i
                      className={`${
                        files[box.id]
                          ? "fas fa-file-alt text-primary"
                          : "flaticon-381-upload-1 text-muted"
                      } fs-24 me-3`}
                    ></i>
                    <div className="text-start">
                      <p
                        className={`mb-0 fs-11 fw-bold ${
                          files[box.id] ? "text-primary text-truncate" : "text-dark"
                        }`}
                        style={files[box.id] ? { maxWidth: "150px" } : {}}
                      >
                        {files[box.id] ? files[box.id].file.name : box.desc}
                      </p>
                      <small className="text-muted" style={{ fontSize: "9px" }}>
                        {files[box.id]
                          ? `${(files[box.id].file.size / 1024 / 1024).toFixed(
                              2
                            )} MB`
                          : box.small}
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </form>
    </CommonModal>
  );
};

export default OrganizationCustomerModal;
